import React, { useState, useEffect } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import AppBar from '@mui/material/AppBar';
import Container from 'components/Container';
import { Topbar, Sidebar, Footer } from './components';
import { getEntitlement } from 'apps/user/Financial/FinancialService';
import { EntitlementInfo } from 'apps/user/Financial/types';

const EntitlementBanner = (): JSX.Element | null => {
  const [info, setInfo] = useState<EntitlementInfo | null>(null);

  useEffect(() => {
    getEntitlement()
      .then((r) => setInfo(r.data.entitlement))
      .catch(() => {
        /* silent — banner is non-critical */
      });
  }, []);

  if (!info) return null;

  const daysUntilTrialEnd = info.trialEndDate
    ? Math.ceil(
      (new Date(info.trialEndDate).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24),
    )
    : null;

  if (
    info.state === 'trialing' &&
    daysUntilTrialEnd !== null &&
    daysUntilTrialEnd <= 7
  ) {
    return (
      <Box
        sx={{
          bgcolor: alpha('#ff8b32', 0.12),
          borderBottom: '1px solid #ff8b32',
          px: 3,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: '#7c4a00', fontWeight: 600 }}>
          Your free trial ends in {daysUntilTrialEnd} day
          {daysUntilTrialEnd !== 1 ? 's' : ''}.
        </Typography>
        <a
          href="/user/subscription"
          style={{
            color: '#1475d1',
            fontWeight: 700,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          Upgrade now →
        </a>
      </Box>
    );
  }

  if (info.state === 'expired' || info.state === 'cancelled') {
    return (
      <Box
        sx={{
          bgcolor: alpha('#ff454d', 0.1),
          borderBottom: '1px solid #ff454d',
          px: 3,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: '#8c0007', fontWeight: 600 }}>
          {info.state === 'expired'
            ? 'Your subscription has expired.'
            : 'Your subscription has been cancelled.'}
        </Typography>
        <a
          href="/user/subscription"
          style={{
            color: '#1475d1',
            fontWeight: 700,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          Resubscribe →
        </a>
      </Box>
    );
  }

  if (info.state === 'needs_attention') {
    return (
      <Box
        sx={{
          bgcolor: alpha('#ff8b32', 0.12),
          borderBottom: '1px solid #ff8b32',
          px: 3,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: '#7c4a00', fontWeight: 600 }}>
          Your subscription needs attention.
        </Typography>
        <a
          href="/user/subscription"
          style={{
            color: '#1475d1',
            fontWeight: 700,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          View details →
        </a>
      </Box>
    );
  }

  return null;
};

interface Props {
  children: React.ReactNode;
}

const UserDashboard = ({ children }: Props): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  const [openSidebar, setOpenSidebar] = React.useState(false);

  const handleSidebarOpen = (): void => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = (): void => {
    setOpenSidebar(false);
  };

  const open = isMd ? false : openSidebar;

  return (
    <Box>
      <AppBar
        position={'fixed'}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
        elevation={0}
      >
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar
        onClose={handleSidebarClose}
        open={open}
        variant={isMd ? 'permanent' : 'temporary'}
      />
      <main>
        <Box height={{ xs: 18, sm: 26, md: 31 }} />
        <Box
          display="flex"
          flex="1 1 auto"
          overflow="hidden"
          paddingLeft={{ md: '256px' }}
        >
          <Box display="flex" flex="1 1 auto" overflow="hidden">
            <Box flex="1 1 auto" height="100%" overflow="auto">
              <EntitlementBanner />
              {children}
              <Divider />
              <Container paddingY={4}>
                <Footer />
              </Container>
            </Box>
          </Box>
        </Box>
      </main>
    </Box>
  );
};

export default UserDashboard;
